import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { InfoOutlined, MapRounded } from "@mui/icons-material";
import { colors } from "../../theme/colors";
import { getMapImageUrl, getMapMetaById } from "../../constants/mapMeta";
import Tooltip from "../common/Tooltip";
import utils from "../../universal/utils";
import { EmptyDataState } from "../common/EmptyDataState";
import { ProfileMapRowsSkeleton } from "./profileSkeletons";

const edgeBasePath = utils.getEdgeBasePath();

function buildMapStats(mapsStats, limit) {
    const entries = Object.entries(mapsStats || {})
        .filter(([, stats]) => stats?.gamesPlayed > 0)
        .sort(([, a], [, b]) => {
            const byPlays = b.gamesPlayed - a.gamesPlayed;
            return byPlays !== 0 ? byPlays : (b.winRate || 0) - (a.winRate || 0);
        });

    const limitedEntries = limit == null ? entries : entries.slice(0, limit);

    return limitedEntries.map(([mapId, stats]) => ({
        mapId,
        gamesPlayed: stats.gamesPlayed,
        winRate: stats.winRate ?? 0,
    }));
}

function MapPerformanceRow({ mapId, gamesPlayed, winRate }) {
    const mapMeta = getMapMetaById(mapId);
    const mapLabel = mapMeta?.display || mapId;
    const mapImageUrl = getMapImageUrl(mapId, edgeBasePath);

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                minWidth: 0,
            }}
        >
            <Box
                sx={{
                    width: "2.5rem",
                    height: "2.5rem",
                    borderRadius: "8px",
                    overflow: "hidden",
                    flexShrink: 0,
                    bgcolor: colors.background.surface,
                    border: `1px solid ${colors.border.default}`,
                }}
            >
                {mapImageUrl ? (
                    <Box
                        component="img"
                        src={mapImageUrl}
                        alt={mapLabel}
                        sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                        }}
                    />
                ) : null}
            </Box>

            <Box sx={{ width: "4.5rem", flexShrink: 0, minWidth: 0 }}>
                <Typography
                    sx={{
                        fontSize: "0.82rem",
                        fontWeight: 600,
                        color: colors.text.primary,
                        lineHeight: 1.2,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    }}
                >
                    {mapLabel}
                </Typography>
                <Typography
                    sx={{
                        mt: "0.15rem",
                        fontSize: "0.68rem",
                        fontWeight: 500,
                        color: "rgba(255,255,255,0.42)",
                        lineHeight: 1.2,
                    }}
                >
                    {gamesPlayed} {gamesPlayed === 1 ? "match" : "matches"}
                </Typography>
            </Box>

            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    height: "0.35rem",
                    borderRadius: "999px",
                    bgcolor: "rgba(255,255,255,0.06)",
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        width: `${Math.max(0, Math.min(100, winRate))}%`,
                        height: "100%",
                        borderRadius: "999px",
                        background: `linear-gradient(90deg, ${colors.primary.active} 0%, ${colors.accent.main} 100%)`,
                    }}
                />
            </Box>

            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    flexShrink: 0,
                }}
            >
                <Typography
                    sx={{
                        fontSize: "0.875rem",
                        fontWeight: 600,
                        color: colors.text.primary,
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                    }}
                >
                    {winRate}%
                </Typography>
                <Tooltip title="Map winrate" placement="top">
                    <Box
                        component="span"
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            color: colors.text.muted,
                            "&:hover": {
                                color: colors.text.secondary,
                            },
                        }}
                    >
                        <InfoOutlined sx={{ fontSize: "0.9rem" }} />
                    </Box>
                </Tooltip>
            </Box>
        </Box>
    );
}

MapPerformanceRow.propTypes = {
    mapId: PropTypes.string.isRequired,
    gamesPlayed: PropTypes.number.isRequired,
    winRate: PropTypes.number.isRequired,
};

export default function OverviewMapPerformance({
    mapsStats = {},
    isLoading = false,
    limit = 3,
}) {
    const maps = useMemo(() => buildMapStats(mapsStats, limit), [mapsStats, limit]);
    const skeletonCount = limit ?? 7;

    if (isLoading) {
        return <ProfileMapRowsSkeleton count={skeletonCount} />;
    }

    if (maps.length === 0) {
        return (
            <EmptyDataState
                title="No map data"
                icon={MapRounded}
                minHeight="6rem"
                embedded
            >
                Best maps unlock after competitive matches
            </EmptyDataState>
        );
    }

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
            }}
        >
            {maps.map((map) => (
                <MapPerformanceRow key={map.mapId} {...map} />
            ))}
        </Box>
    );
}

OverviewMapPerformance.propTypes = {
    mapsStats: PropTypes.object,
    isLoading: PropTypes.bool,
    limit: PropTypes.number,
};
